// HTTP-backed Deal Calc storage adapter — async twin of the React
// standalone's `createLocalStorageAdapter()`. Read
// `docs/dealcalc/deal_storage_contract.md` for the data shape and
// migration discipline this adapter implements.
//
// Per-row CRUD against `/api/deals/`, plus a single state read against
// `/api/deals/state/` for hydration, plus `/api/deal-prefs/<key>/` for
// the small string preferences the editor persists. The migration-merge
// happens client-side via `mergeStateWithTemplate()` so old stored deals
// always come back with the canonical Deal shape.

import axios from 'axios';

import {createBlankDealState, IDealState, mergeStateWithTemplate} from './defaults';

const STANDARD_HEADERS = {
	'Accept': 'application/json',
	'Content-Type': 'application/json',
};

const XSRF_CFG = {
	xsrfCookieName: 'csrftoken',
	xsrfHeaderName: 'X-CSRFToken',
};

export type DealSlug = string;

export interface IDeal {
	readonly id: number;
	readonly slug: DealSlug;
	readonly createdAt?: string;
	readonly lastModified?: string;
	readonly projectBlockId: number | null;
	// Slug of the project this Deal is attached to (via `project_block`),
	// or null for standalone deals. Set server-side from
	// `project_block.project.slug`.
	readonly projectSlug?: string | null;
	parcelUid: string | null;
	parcelSnapshotJson: Record<string, any>;
	title: string;
	stateJson: IDealState;
}

export interface INewDeal {
	title?: string;
	parcelUid?: string | null;
	parcelSnapshotJson?: Record<string, any>;
	stateJson?: Partial<IDealState>;
	projectBlockId?: number | null;
	// Phase 5B: when set on a *create* call, the API resolves the
	// project, creates a `ProjectBlock(type=DEAL)` row on it, and links
	// the new Deal to that block. Ignored on update.
	projectSlug?: string | null;
}

export interface IStoredState {
	deals: Record<DealSlug, IDeal>;
	dealOrder: Array<DealSlug>;
	activeDealId: DealSlug | null;
	schemaVersion: number;
	template?: IDealState;
}

interface IServerStateResponse {
	deals: Record<DealSlug, any>;
	dealOrder: Array<DealSlug>;
	activeDealId: DealSlug | null;
	schemaVersion: number;
	template?: any;
}

function hydrateDeal(raw: any): IDeal {
	return {
		...raw,
		stateJson: mergeStateWithTemplate(raw && raw.stateJson),
	};
}

export interface IDealStorage {
	loadState(opts?: {projectSlug?: string | null}): Promise<IStoredState | null>;
	createDeal(payload: INewDeal): Promise<IDeal>;
	updateDeal(slug: DealSlug, payload: INewDeal): Promise<IDeal>;
	deleteDeal(slug: DealSlug): Promise<void>;
	getPref(key: string): Promise<string | null>;
	setPref(key: string, value: string): Promise<void>;
	fetchParcel(uid: string): Promise<IParcel | null>;
}

export class HTTPDealStorage implements IDealStorage {
	async loadState(opts?: {projectSlug?: string | null}): Promise<IStoredState | null> {
		try {
			// Project-scoped pages forward their slug so the server filters
			// to deals attached to that project. Prevents the
			// /<project_slug>/deals/<deal_slug>/ family from rendering deals
			// that belong to a different project the user also owns.
			const projectSlug = opts && opts.projectSlug ? opts.projectSlug : null;
			const url = projectSlug
				? `/api/deals/state/?project_slug=${encodeURIComponent(projectSlug)}`
				: '/api/deals/state/';
			const rv = await axios.request<IServerStateResponse>({
				method: 'GET',
				url,
				headers: STANDARD_HEADERS,
				responseType: 'json',
			});
			const body = rv.data;
			const deals: Record<DealSlug, IDeal> = {};
			for (const slug of Object.keys(body.deals || {})) {
				deals[slug] = hydrateDeal(body.deals[slug]);
			}
			return {
				deals,
				dealOrder: body.dealOrder || [],
				activeDealId: body.activeDealId,
				schemaVersion: body.schemaVersion || 1,
				template: body.template ? mergeStateWithTemplate(body.template) : createBlankDealState(),
			};
		} catch {
			return null;
		}
	}

	async createDeal(payload: INewDeal): Promise<IDeal> {
		const rv = await axios.request<any>({
			method: 'POST',
			url: '/api/deals/',
			headers: STANDARD_HEADERS,
			data: payload,
			responseType: 'json',
			...XSRF_CFG,
		});
		return hydrateDeal(rv.data);
	}

	async updateDeal(slug: DealSlug, payload: INewDeal): Promise<IDeal> {
		const rv = await axios.request<any>({
			method: 'PUT',
			url: `/api/deals/${encodeURIComponent(slug)}/`,
			headers: STANDARD_HEADERS,
			data: payload,
			responseType: 'json',
			...XSRF_CFG,
		});
		return hydrateDeal(rv.data);
	}

	async deleteDeal(slug: DealSlug): Promise<void> {
		await axios.request<void>({
			method: 'DELETE',
			url: `/api/deals/${encodeURIComponent(slug)}/`,
			headers: STANDARD_HEADERS,
			responseType: 'json',
			...XSRF_CFG,
		});
	}

	async getPref(key: string): Promise<string | null> {
		try {
			const rv = await axios.request<{key: string; value: string | null}>({
				method: 'GET',
				url: `/api/deal-prefs/${encodeURIComponent(key)}/`,
				headers: STANDARD_HEADERS,
				responseType: 'json',
			});
			return rv.data && rv.data.value !== undefined ? rv.data.value : null;
		} catch {
			return null;
		}
	}

	async setPref(key: string, value: string): Promise<void> {
		try {
			await axios.request<void>({
				method: 'PUT',
				url: `/api/deal-prefs/${encodeURIComponent(key)}/`,
				headers: STANDARD_HEADERS,
				data: {value: value},
				responseType: 'json',
				...XSRF_CFG,
			});
		} catch {
			// Persistence failures must not crash the editor mid-session.
		}
	}

	// Fetches a parcel detail payload from the existing /api/parcels/
	// endpoint. Used by the editor's parcel-pre-populate flow when the
	// user lands on /deals/new/?parcel=<uid> from Project Detail or the
	// dashboard. Returns null on any error so the editor falls through
	// to the blank-deal path instead of blocking on parcel fetch
	// failures.
	async fetchParcel(uid: string): Promise<IParcel | null> {
		try {
			const rv = await axios.request<IParcel>({
				method: 'GET',
				url: `/api/parcels/${encodeURIComponent(uid)}/`,
				headers: STANDARD_HEADERS,
				responseType: 'json',
			});
			return rv.data;
		} catch {
			return null;
		}
	}
}

// Coalesces rapid per-deal saves into a single trailing request,
// matching the React standalone's 500 ms debounce on saveState.
export class DebouncedDealWriter {
	private pending: Map<DealSlug, INewDeal> = new Map();
	private timers: Map<DealSlug, number> = new Map();

	constructor(private storage: IDealStorage, private waitMs: number = 500) {}

	scheduleUpdate(slug: DealSlug, payload: INewDeal): void {
		this.pending.set(slug, {...(this.pending.get(slug) || {}), ...payload});
		const existing = this.timers.get(slug);
		if (existing !== undefined) {
			window.clearTimeout(existing);
		}
		const handle = window.setTimeout(() => {
			this.flush(slug);
		}, this.waitMs);
		this.timers.set(slug, handle);
	}

	async flush(slug: DealSlug): Promise<IDeal | null> {
		const payload = this.pending.get(slug);
		this.pending.delete(slug);
		const t = this.timers.get(slug);
		if (t !== undefined) {
			window.clearTimeout(t);
			this.timers.delete(slug);
		}
		if (!payload) {
			return null;
		}
		try {
			return await this.storage.updateDeal(slug, payload);
		} catch {
			return null;
		}
	}

	async flushAll(): Promise<void> {
		const slugs = Array.from(this.pending.keys());
		await Promise.all(slugs.map(s => this.flush(s)));
	}
}
